import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Container,
    Paper,
    Typography,
    Chip,
    IconButton,
    TextField,
    Stack,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    useTheme,
    useMediaQuery,
    Avatar,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Collapse,
    Alert,
    Card,
    CardContent,
    CardActions,
    Tooltip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Skeleton
} from '@mui/material';
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
    useDroppable,
    DragOverlay,
} from '@dnd-kit/core';
import {
    SortableContext,
    verticalListSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import RefreshIcon from '@mui/icons-material/Refresh';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import PeopleIcon from '@mui/icons-material/People';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WorkIcon from '@mui/icons-material/Work';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningIcon from '@mui/icons-material/Warning';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import MapIcon from '@mui/icons-material/Map';
import BadgeIcon from '@mui/icons-material/Badge';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import CakeIcon from '@mui/icons-material/Cake';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CloseIcon from '@mui/icons-material/Close';
import { FlagFocus, primarySpot } from '../utils/flagFocus';
import apiClient from '../api';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AuthorizationsTable from '../components/recruitment/AuthorizationsTable';
import ApplicantMapDialog from '../components/recruitment/ApplicantMapDialog';

const US_STATE_CODES = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA',
    'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK',
    'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC',
];

// Resume upload constraints (must stay in sync with server: server/routes/intakeAdmin.js)
const RESUME_MAX_BYTES = 10 * 1024 * 1024; // 10 MB
const ACCEPTED_RESUME_EXTS = ['.pdf', '.doc', '.docx'];
const ACCEPTED_RESUME_MIMES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isEmailValid(value) {
    if (!value) return true; // optional
    return EMAIL_REGEX.test(String(value).trim());
}

function phoneDigits(value) {
    return String(value || '').replace(/\D+/g, '');
}

function isPhoneValid(value) {
    if (!value) return true; // optional
    const d = phoneDigits(value);
    if (d.length === 10) return true;
    if (d.length === 11 && d.startsWith('1')) return true;
    if (d.length >= 7 && d.length <= 15) return true;
    return false;
}

function formatPhonePreview(value) {
    const d = phoneDigits(value);
    if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
    if (d.length === 11 && d.startsWith('1')) {
        return `+1 (${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
    }
    if (d.length >= 7) return `+${d}`;
    return '';
}

function formatBytes(bytes) {
    if (!bytes && bytes !== 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function validateResumeFile(file) {
    if (!file) return null;
    const name = String(file.name || '');
    const dotIndex = name.lastIndexOf('.');
    const ext = dotIndex >= 0 ? name.slice(dotIndex).toLowerCase() : '';
    const typeOk =
        (ext && ACCEPTED_RESUME_EXTS.includes(ext)) ||
        (file.type && ACCEPTED_RESUME_MIMES.includes(file.type));
    if (!typeOk) return 'Only PDF, DOC, or DOCX files are allowed.';
    if (file.size > RESUME_MAX_BYTES) {
        return `File is too large (${formatBytes(file.size)}). Max ${formatBytes(RESUME_MAX_BYTES)}.`;
    }
    return null;
}

function describeApiError(err) {
    if (!err) return 'Unknown error.';
    if (!err.response) {
        if (err.code === 'ECONNABORTED') return 'Request timed out. Please try again.';
        return 'Network error: could not reach server.';
    }
    const { status, data, statusText } = err.response;
    if (status === 413) {
        return 'Resume file is too large for the server (max 10 MB). Try a smaller file.';
    }
    if (status === 401) return 'Your session has expired. Please log in again.';
    if (status === 403) return 'You do not have permission to perform this action.';
    if (typeof data === 'string' && data.trim()) {
        const stripped = data.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
        return `Server error (${status}). ${stripped.slice(0, 200)}`;
    }
    if (data && typeof data === 'object') {
        return data.message || data.error || `Request failed (${status} ${statusText || ''}).`.trim();
    }
    return `Request failed (${status}${statusText ? ' ' + statusText : ''}).`;
}

function DetailField({ label, value }) {
    if (value === undefined || value === null || value === '') return null;
    return (
        <Box>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
            <Typography variant="body2">{String(value)}</Typography>
        </Box>
    );
}

function MaskedField({ label, value }) {
    const [visible, setVisible] = React.useState(false);
    if (!value) return null;
    const masked = String(value).replace(/.(?=.{4})/g, '*');
    return (
        <Box>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
            <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{visible ? value : masked}</Typography>
                <IconButton size="small" onClick={() => setVisible(v => !v)}>
                    {visible ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                </IconButton>
            </Stack>
        </Box>
    );
}

function DetailAccordion({ title, children, defaultExpanded }) {
    return (
        <Accordion disableGutters defaultExpanded={defaultExpanded} sx={{ boxShadow: 'none', border: '1px solid', borderColor: 'divider', borderRadius: '8px !important', mb: 1, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2" fontWeight={700}>{title}</Typography>
            </AccordionSummary>
            <AccordionDetails>{children}</AccordionDetails>
        </Accordion>
    );
}

function ApplicationDetailContent({ detailItem, setDetailItem, handleUpdate, flags }) {
    const sp = detailItem.sourcePayload || {};
    const loc = sp.location || {};
    const ec = sp.emergency_contact || {};
    const lic = sp.license || {};
    const addrHist = Array.isArray(sp.address_history) ? sp.address_history : [];
    const empHist = Array.isArray(sp.employment_history) ? sp.employment_history : [];
    const drivExp = sp.driving_experience || {};
    const licHist = sp.license_history || {};
    const hasAny = (obj) => obj && typeof obj === 'object' && Object.values(obj).some(v => typeof v === 'string' ? v.trim() : v);
    const safe = (v) => (v === undefined || v === null || v === '' ? '—' : String(v));
    const apiOrigin = (apiClient?.defaults?.baseURL || '').replace(/\/api\/?$/, '');
    const resumeFileHref = detailItem.resumeFilePath && apiOrigin ? `${apiOrigin}${detailItem.resumeFilePath}` : null;
    const focus = primarySpot(flags);

    return (
            <Stack spacing={2}>
            {detailItem.referrerName && (
                <Chip
                    label={detailItem.referralKind === 'possible' ? `Possible referral from ${detailItem.referrerName}` : `Referred by ${detailItem.referrerName}`}
                    sx={{ alignSelf: 'flex-start', fontWeight: 700, bgcolor: detailItem.referralKind === 'possible' ? undefined : '#2e7d32', color: detailItem.referralKind === 'possible' ? undefined : '#fff' }}
                    color={detailItem.referralKind === 'possible' ? 'warning' : 'default'}
                />
            )}
            {/* Core info grid */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box>
                    <Typography variant="caption" color="text.secondary">Submitted</Typography>
                    <Typography variant="body1">{new Date(detailItem.createdAt).toLocaleString()}</Typography>
                </Box>
                <Box>
                    <Typography variant="caption" color="text.secondary">Website</Typography>
                    <Typography variant="body1">{detailItem.websiteDomain || '—'}</Typography>
                </Box>
                <Box>
                    <Typography variant="caption" color="text.secondary">First Name</Typography>
                    <TextField size="small" fullWidth value={detailItem.firstName || ''} onChange={(e) => setDetailItem({ ...detailItem, firstName: e.target.value })} onBlur={(e) => handleUpdate(detailItem._id, { firstName: e.target.value })} />
                </Box>
                {sp.middle_name && (
                    <Box>
                        <Typography variant="caption" color="text.secondary">Middle Name</Typography>
                        <Typography variant="body1">{sp.middle_name}</Typography>
                    </Box>
                )}
                <Box>
                    <Typography variant="caption" color="text.secondary">Last Name</Typography>
                    <TextField size="small" fullWidth value={detailItem.lastName || ''} onChange={(e) => setDetailItem({ ...detailItem, lastName: e.target.value })} onBlur={(e) => handleUpdate(detailItem._id, { lastName: e.target.value })} />
                </Box>
                <FlagFocus spot="email" flags={flags} scroll={focus === 'email'}>
                    <Box>
                        <Typography variant="caption" color="text.secondary">Email</Typography>
                        <TextField size="small" fullWidth value={detailItem.email || ''} onChange={(e) => setDetailItem({ ...detailItem, email: e.target.value })} onBlur={(e) => handleUpdate(detailItem._id, { email: e.target.value })} />
                    </Box>
                </FlagFocus>
                <FlagFocus spot="phone" flags={flags} scroll={focus === 'phone'}>
                    <Box>
                        <Typography variant="caption" color="text.secondary">Phone</Typography>
                        <TextField size="small" fullWidth value={detailItem.phone || ''} onChange={(e) => setDetailItem({ ...detailItem, phone: e.target.value })} onBlur={(e) => handleUpdate(detailItem._id, { phone: e.target.value })} />
                    </Box>
                </FlagFocus>
                <Box>
                    <Typography variant="caption" color="text.secondary">CDL Class</Typography>
                    <Typography variant="body1">{sp.cdl_class || '—'}</Typography>
                </Box>
                <Box>
                    <Typography variant="caption" color="text.secondary">Experience (Years)</Typography>
                    <Typography variant="body1">{sp.experience_years ?? '—'}</Typography>
                </Box>
                <Box>
                    <Typography variant="caption" color="text.secondary">Preferred Division</Typography>
                    <Typography variant="body1">{sp.preferred_division || '—'}</Typography>
                </Box>
                <FlagFocus spot="address" flags={flags} scroll={focus === 'address'} sx={{ gridColumn: '1 / -1' }}>
                    <Box sx={{ gridColumn: '1 / -1' }}>
                        <Typography variant="caption" color="text.secondary">Location</Typography>
                        <Typography variant="body1">
                            {[loc.street, loc.suite].filter(Boolean).join(', ')}
                            {(loc.street || loc.suite) && <br />}
                            {[loc.city, loc.state, loc.postal_code].filter(Boolean).join(', ') || '—'}
                        </Typography>
                    </Box>
                </FlagFocus>
                {sp.currently_employed !== undefined && (
                    <Box>
                        <Typography variant="caption" color="text.secondary">Currently Employed</Typography>
                        <Typography variant="body1">{sp.currently_employed ? 'Yes' : 'No'}</Typography>
                    </Box>
                )}
                {sp.message && (
                    <Box sx={{ gridColumn: '1 / -1' }}>
                        <Typography variant="caption" color="text.secondary">Message</Typography>
                        <Typography variant="body1">{sp.message}</Typography>
                    </Box>
                )}
                <Box sx={{ gridColumn: '1 / -1' }}>
                    <Typography variant="caption" color="text.secondary">Status</Typography>
                    <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                        <Select
                            value={detailItem.status || 'new'}
                            onChange={(e) => {
                                const newStatus = e.target.value;
                                handleUpdate(detailItem._id, { status: newStatus });
                            }}
                        >
                            <MenuItem value="new">New</MenuItem>
                            <MenuItem value="in_review">In Review</MenuItem>
                            <MenuItem value="approved">Approved for Hire</MenuItem>
                            <MenuItem value="hired">Hired</MenuItem>
                            <MenuItem value="rejected">Rejected</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
                <Box sx={{ gridColumn: '1 / -1' }}>
                    <Typography variant="caption" color="text.secondary">Notes</Typography>
                    <TextField
                        size="small"
                        fullWidth
                        multiline
                        rows={3}
                        value={detailItem.notes || ''}
                        onChange={(e) => setDetailItem({ ...detailItem, notes: e.target.value })}
                        onBlur={(e) => handleUpdate(detailItem._id, { notes: e.target.value })}
                        placeholder="Add internal notes..."
                    />
                </Box>
            </Box>

            <Divider />

            {(resumeFileHref || detailItem.resumeUrl) && (
                <DetailAccordion title="Resume" defaultExpanded>
                    <Stack spacing={1}>
                        {resumeFileHref && (
                            <Button
                                variant="outlined"
                                size="small"
                                component="a"
                                href={resumeFileHref}
                                target="_blank"
                                rel="noreferrer"
                            >
                                View / Download uploaded resume
                            </Button>
                        )}
                        {detailItem.resumeUrl && (
                            <Button
                                variant="outlined"
                                size="small"
                                component="a"
                                href={detailItem.resumeUrl}
                                target="_blank"
                                rel="noreferrer"
                            >
                                Open resume link
                            </Button>
                        )}
                        {detailItem.resumeOriginalName && (
                            <Typography variant="caption" color="text.secondary">
                                File: {detailItem.resumeOriginalName}
                            </Typography>
                        )}
                    </Stack>
                </DetailAccordion>
            )}

            {/* Sensitive Personal Info */}
            {(sp.date_of_birth || sp.social_security) && (
                <DetailAccordion title="Sensitive Personal Info">
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                        <DetailField label="Date of Birth" value={sp.date_of_birth} />
                        <MaskedField label="Social Security" value={sp.social_security} />
                    </Box>
                </DetailAccordion>
            )}

            {/* Emergency Contact */}
            {hasAny(ec) && (
                <DetailAccordion title="Emergency Contact" defaultExpanded>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                        <DetailField label="Name" value={ec.name} />
                        <DetailField label="Phone" value={ec.phone} />
                        <DetailField label="Secondary Phone" value={ec.secondary_phone} />
                        <DetailField label="Relation" value={ec.relation} />
                    </Box>
                </DetailAccordion>
            )}

            {/* License Details */}
            {hasAny(lic) && (
                <DetailAccordion title="License Details" defaultExpanded>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                        <DetailField label="State" value={lic.state} />
                        <DetailField label="Number" value={lic.number} />
                        <DetailField label="Type" value={lic.type} />
                        <DetailField label="Expiration" value={lic.expiration} />
                    </Box>
                </DetailAccordion>
            )}

            {/* Address History */}
            {addrHist.length > 0 && (
                <DetailAccordion title="Address History">
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>#</TableCell>
                                    <TableCell>Street</TableCell>
                                    <TableCell>City</TableCell>
                                    <TableCell>State / Zip</TableCell>
                                    <TableCell>Years</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {addrHist.map((a, i) => (
                                    <TableRow key={i}>
                                        <TableCell>{i + 1}</TableCell>
                                        <TableCell>{safe(a.street)}</TableCell>
                                        <TableCell>{safe(a.city)}</TableCell>
                                        <TableCell>{safe(a.state_zip)}</TableCell>
                                        <TableCell>{safe(a.years)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DetailAccordion>
            )}

            {/* Employment History */}
            {empHist.length > 0 && (
                <DetailAccordion title="Employment History">
                    {empHist.map((e, i) => (
                        <Paper key={i} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                                {i + 1}. {safe(e.name)} {e.from || e.to ? `(${safe(e.from)} — ${safe(e.to)})` : ''}
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                                <DetailField label="Phone" value={e.phone} />
                                <DetailField label="Address" value={e.address} />
                                <DetailField label="Supervisor" value={e.supervisor} />
                                <DetailField label="Supervisor Phone" value={e.supervisor_phone} />
                                <DetailField label="Position" value={e.position} />
                                <DetailField label="Salary" value={e.salary} />
                                <DetailField label="Reason for Leaving" value={e.reason_leaving} />
                                <DetailField label="Gaps Explanation" value={e.gaps_explanation} />
                                <DetailField label="Subject to FMCSR" value={e.fmcsr} />
                                <DetailField label="Safety Sensitive Function" value={e.safety_sensitive} />
                            </Box>
                        </Paper>
                    ))}
                </DetailAccordion>
            )}

            {/* Driving Experience */}
            {hasAny(drivExp) && (
                <DetailAccordion title="Driving Experience">
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Equipment</TableCell>
                                    <TableCell>From</TableCell>
                                    <TableCell>To</TableCell>
                                    <TableCell>Miles</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {[
                                    { label: 'Straight Truck', data: drivExp.straight_truck },
                                    { label: 'Tractor & Semi-Trailer', data: drivExp.tractor_semi },
                                    { label: 'Tractor & Two Trailers', data: drivExp.tractor_two_trailer },
                                    { label: 'Other', data: drivExp.other },
                                ].filter(r => r.data && hasAny(r.data)).map((r, i) => (
                                    <TableRow key={i}>
                                        <TableCell>{r.label}</TableCell>
                                        <TableCell>{safe(r.data.equipment)}</TableCell>
                                        <TableCell>{safe(r.data.from)}</TableCell>
                                        <TableCell>{safe(r.data.to)}</TableCell>
                                        <TableCell>{safe(r.data.miles)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DetailAccordion>
            )}

            {/* License History */}
            {hasAny(licHist) && (
                <DetailAccordion title="License History">
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                        <DetailField label="License Denied" value={licHist.denied} />
                        {licHist.denied === 'yes' && <DetailField label="Denied Explanation" value={licHist.denied_explanation} />}
                        <DetailField label="License Suspended" value={licHist.suspended} />
                        {licHist.suspended === 'yes' && <DetailField label="Suspended Explanation" value={licHist.suspended_explanation} />}
                    </Box>
                </DetailAccordion>
            )}

            {/* Accident Record / Traffic Convictions */}
            {(sp.accident_record || sp.traffic_convictions) && (
                <DetailAccordion title="Violations & Accident Record">
                    <Stack spacing={1.5}>
                        {sp.accident_record && (
                            <Box>
                                <Typography variant="caption" color="text.secondary">Accident Record</Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{sp.accident_record}</Typography>
                            </Box>
                        )}
                        {sp.traffic_convictions && (
                            <Box>
                                <Typography variant="caption" color="text.secondary">Traffic Convictions</Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{sp.traffic_convictions}</Typography>
                            </Box>
                        )}
                    </Stack>
                </DetailAccordion>
            )}

            {/* Authorization Documents */}
            <AuthorizationsTable
                backgroundEsign={sp.authorizations_esign?.background_check}
                pspEsign={sp.authorizations_esign?.psp}
                applicant={{ firstName: detailItem.firstName, lastName: detailItem.lastName }}
                sourcePayload={sp}
            />
        </Stack>
    );
}

function BoardCardSkeletons() {
    return (
        <Stack spacing={1}>
            {[0, 1, 2].map((i) => (
                <Paper key={i} variant="outlined" sx={{ p: 1.25, borderRadius: 1 }}>
                    <Skeleton variant="text" width="68%" height={22} />
                    <Skeleton variant="text" width="46%" height={16} />
                    <Skeleton variant="text" width="54%" height={16} />
                </Paper>
            ))}
        </Stack>
    );
}

function RecruitmentPage() {
    const { user } = useAuth();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const location = useLocation();

    const [resumes, setResumes] = useState([]);
    const [openFlags, setOpenFlags] = useState([]);
    const [loading, setLoading] = useState(false);
    const [ready, setReady] = useState(false);
    const [search, setSearch] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [error, setError] = useState(null);

    const [manualOpen, setManualOpen] = useState(false);
    const [manualSaving, setManualSaving] = useState(false);
    const [manualForm, setManualForm] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        notes: '',
        status: 'new',
        experience_years: '',
        locationState: '',
        cdl_class: '',
        date_of_birth: '',
    });
    const [manualFile, setManualFile] = useState(null);
    const [manualError, setManualError] = useState(null);
    const [manualFileError, setManualFileError] = useState(null);
    const [dragActive, setDragActive] = useState(false);
    const fileInputRef = React.useRef(null);

    const emailError = manualForm.email && !isEmailValid(manualForm.email)
        ? 'Enter a valid email (e.g. name@example.com).'
        : '';
    const phoneError = manualForm.phone && !isPhoneValid(manualForm.phone)
        ? 'Enter a valid phone number (10 digits or include country code).'
        : '';
    const phonePreview = manualForm.phone && !phoneError ? formatPhonePreview(manualForm.phone) : '';
    const expError = (() => {
        const raw = (manualForm.experience_years || '').toString().trim();
        if (!raw) return '';
        const n = Number(raw);
        if (Number.isNaN(n) || !Number.isFinite(n)) return 'Must be a number.';
        if (n < 0 || n > 80) return 'Must be between 0 and 80.';
        return '';
    })();

    const canSubmitManual =
        !!manualForm.firstName.trim() &&
        !!manualForm.lastName.trim() &&
        !emailError &&
        !phoneError &&
        !expError &&
        !manualFileError &&
        !manualSaving;

    const [detailOpen, setDetailOpen] = useState(false);
    const [detailItem, setDetailItem] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [activeId, setActiveId] = useState(null);
    const [mapOpen, setMapOpen] = useState(false);

    // Kanban columns
    const columns = [
        { id: 'new', title: 'New Applications', color: '#1976d2', icon: <PersonAddIcon /> },
        { id: 'in_review', title: 'In Review', color: '#f57c00', icon: <AssignmentIndIcon /> },
        { id: 'approved', title: 'Approved for Hire', color: '#2e7d32', icon: <CheckCircleIcon /> },
        { id: 'hired', title: 'Hired', color: '#6a1b9a', icon: <HowToRegIcon /> },
        { id: 'rejected', title: 'Rejected', color: '#d32f2f', icon: <CancelIcon /> }
    ];


    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const [res, flagRes] = await Promise.all([
                apiClient.get('/intake-admin/resumes', {
                    params: { page: 1, limit: 1000, search }
                }),
                apiClient.get('/anomalies', { params: { status: 'open', source: 'resume' } }).catch(() => ({ data: { data: [] } })),
            ]);
            const data = res.data.data || { items: [] };
            setResumes(data.items);
            setOpenFlags(flagRes.data?.data || []);
        } catch (err) {
            console.error('Error fetching resumes:', err);
            setError('Failed to load applications. Please try again.');
        } finally {
            setLoading(false);
            setReady(true);
        }
    };

    useEffect(() => {
        fetchData();
    }, [search]);

    const resetManualForm = () => {
        setManualForm({
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            notes: '',
            status: 'new',
            experience_years: '',
            locationState: '',
            cdl_class: '',
            date_of_birth: '',
        });
        setManualFile(null);
        setManualError(null);
        setManualFileError(null);
        setDragActive(false);
    };

    const handleManualFileSelected = (file) => {
        if (!file) {
            setManualFile(null);
            setManualFileError(null);
            return;
        }
        const validationMsg = validateResumeFile(file);
        if (validationMsg) {
            setManualFileError(validationMsg);
            setManualFile(null);
        } else {
            setManualFileError(null);
            setManualFile(file);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!dragActive) setDragActive(true);
    };
    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
    };
    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        const file = e.dataTransfer?.files?.[0];
        if (file) handleManualFileSelected(file);
    };

    const handleManualSubmit = async () => {
        setManualSaving(true);
        setManualError(null);

        if (!manualForm.firstName.trim() || !manualForm.lastName.trim()) {
            setManualError('First name and last name are required.');
            setManualSaving(false);
            return;
        }
        if (emailError) {
            setManualError(emailError);
            setManualSaving(false);
            return;
        }
        if (phoneError) {
            setManualError(phoneError);
            setManualSaving(false);
            return;
        }
        if (expError) {
            setManualError(`Years of experience: ${expError}`);
            setManualSaving(false);
            return;
        }
        if (manualFileError) {
            setManualError(manualFileError);
            setManualSaving(false);
            return;
        }

        try {
            const fd = new FormData();
            fd.append('firstName', manualForm.firstName.trim());
            fd.append('lastName', manualForm.lastName.trim());
            if (manualForm.email) fd.append('email', manualForm.email.trim());
            if (manualForm.phone) fd.append('phone', manualForm.phone.trim());
            if (manualForm.notes) fd.append('notes', manualForm.notes);
            if (manualForm.status) fd.append('status', manualForm.status);
            if ((manualForm.experience_years || '').toString().trim() !== '') {
                fd.append('experience_years', String(manualForm.experience_years).trim());
            }
            if ((manualForm.locationState || '').trim()) {
                fd.append('locationState', manualForm.locationState.trim().toUpperCase());
            }
            if (manualForm.cdl_class) fd.append('cdl_class', manualForm.cdl_class);
            if (manualForm.date_of_birth) fd.append('date_of_birth', manualForm.date_of_birth);
            if (manualFile) fd.append('file', manualFile);

            await apiClient.post('/intake-admin/resumes', fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            setManualOpen(false);
            resetManualForm();
            await fetchData();
        } catch (err) {
            const msg = describeApiError(err);
            console.error('[Recruitment] Failed to add applicant', {
                message: msg,
                status: err?.response?.status,
                statusText: err?.response?.statusText,
                data: err?.response?.data,
                code: err?.code,
                errMessage: err?.message,
            });
            setManualError(msg);
        } finally {
            setManualSaving(false);
        }
    };

    const markViewed = async (id) => {
        try {
            await apiClient.post(`/intake-admin/resumes/${id}/viewed`);
            fetchData();
        } catch (err) {
            console.error('Error marking as viewed:', err);
        }
    };

    const handleUpdate = async (id, updates) => {
        try {
            await apiClient.patch(`/intake-admin/resumes/${id}`, updates);
            fetchData();
            if (detailItem && detailItem._id === id) {
                setDetailItem({ ...detailItem, ...updates });
            }
        } catch (err) {
            console.error('Error updating resume:', err);
            setError('Failed to update application.');
        }
    };

    const handleCardClick = async (item) => {
        try {
            const res = await apiClient.get(`/intake-admin/resumes/${item._id}`);
            setDetailItem(res.data?.data?.item || item);
        } catch (err) {
            console.error('Error loading application detail:', err);
            setDetailItem(item);
        }
        setDetailOpen(true);
        markViewed(item._id);
    };

    const flaggedResumeIds = new Set(openFlags.map((flag) => String(flag.sourceId)));
    const openedFlagId = useRef('');

    useEffect(() => {
        if (loading) return;
        const params = new URLSearchParams(location.search || '');
        const id = params.get('id');
        if (!id || openedFlagId.current === id) return;
        const item = resumes.find((resume) => String(resume._id) === id);
        if (!item) return;
        openedFlagId.current = id;
        handleCardClick(item);
    }, [loading, location.search, resumes]);

    const handleDeleteRequest = (item, e) => {
        e.stopPropagation();
        setItemToDelete(item);
        setConfirmOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!itemToDelete) return;
        try {
            await apiClient.delete(`/intake-admin/resumes/${itemToDelete._id}`);
            setConfirmOpen(false);
            setItemToDelete(null);
            fetchData();
        } catch (err) {
            console.error('Error deleting resume:', err);
            setError('Failed to delete application.');
        }
    };

    const getItemsByStatus = (status) => {
        return resumes.filter(r => r.status === status);
    };

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        })
    );

    const handleDragStart = (event) => {
        setActiveId(event.active.id);
    };

    const handleDragEnd = async (event) => {
        setActiveId(null);
        if (!event.over) return;

        const resumeId = event.active.id;
        const resume = resumes.find(r => r._id === resumeId);
        if (!resume) return;

        let newStatus = null;
        const isColumn = columns.find(c => c.id === event.over.id);
        if (isColumn) {
            newStatus = event.over.id;
        } else {
            const targetResume = resumes.find(r => r._id === event.over.id);
            if (targetResume) newStatus = targetResume.status;
        }

        if (!newStatus || resume.status === newStatus) return;

        try {
            await handleUpdate(resumeId, { status: newStatus });
        } catch (err) {
            console.error('Error updating resume status:', err);
            setError('Failed to update application status.');
        }
    };

    const renderStatsCards = () => {
        const stats = {
            total: resumes.length,
            new: resumes.filter(r => r.status === 'new').length,
            in_review: resumes.filter(r => r.status === 'in_review').length,
            approved: resumes.filter(r => r.status === 'approved').length,
            hired: resumes.filter(r => r.status === 'hired').length,
            rejected: resumes.filter(r => r.status === 'rejected').length
        };

        const experienceValues = resumes
            .map(r => r.sourcePayload?.experience_years)
            .filter(v => v != null && !isNaN(v));
        const avgExperience = experienceValues.length
            ? (experienceValues.reduce((a, b) => a + Number(b), 0) / experienceValues.length).toFixed(1)
            : '—';

        const states = resumes
            .map(r => r.sourcePayload?.location?.state)
            .filter(Boolean);
        const stateCounts = {};
        states.forEach(s => { stateCounts[s] = (stateCounts[s] || 0) + 1; });
        const topState = Object.entries(stateCounts).sort((a, b) => b[1] - a[1])[0];

        const cdlValues = resumes
            .map(r => r.sourcePayload?.cdl_class)
            .filter(Boolean);
        const cdlCounts = {};
        cdlValues.forEach(c => { cdlCounts[c] = (cdlCounts[c] || 0) + 1; });
        const topCdl = Object.entries(cdlCounts).sort((a, b) => b[1] - a[1])[0];

        const now = new Date();
        const ages = resumes
            .map(r => r.sourcePayload?.date_of_birth)
            .filter(Boolean)
            .map(dob => {
                const d = new Date(dob);
                if (isNaN(d.getTime())) return null;
                let age = now.getFullYear() - d.getFullYear();
                const m = now.getMonth() - d.getMonth();
                if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
                return age > 0 && age < 100 ? age : null;
            })
            .filter(v => v != null);
        const avgAge = ages.length
            ? Math.round(ages.reduce((a, b) => a + b, 0) / ages.length)
            : '—';

        const largePad = { p: { xs: 1.25, md: 1.5 } };
        const largeAvatarSx = { bgcolor: 'rgba(255,255,255,0.2)', width: 36, height: 36 };
        const largeIconSx = { fontSize: 20 };
        const statLabelSx = {
            fontWeight: 600,
            opacity: 0.9,
            fontSize: '0.65rem',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            lineHeight: 1.2,
            display: 'block',
        };

        const pulse = { bgcolor: 'rgba(255,255,255,0.35)' };
        const bigStat = (value) => (ready ? (
            <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.1, mt: 0.25 }}>{value}</Typography>
        ) : (
            <Skeleton variant="text" width={40} height={32} sx={{ ...pulse, mt: 0.25 }} />
        ));
        const smallStat = (value) => (ready ? (
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1, mt: 0.25 }}>{value}</Typography>
        ) : (
            <Skeleton variant="text" width={56} height={28} sx={{ ...pulse, mt: 0.25 }} />
        ));

        const smallCardSx = { p: { xs: 1, md: 1.25 } };
        const smallAvatarSx = { bgcolor: 'rgba(255,255,255,0.2)', width: { xs: 28, md: 30 }, height: { xs: 28, md: 30 } };
        const smallIconSx = { fontSize: { xs: 16, md: 17 } };

        return (
            <Box sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap sx={{ rowGap: 1.25, columnGap: 1.25 }}>
                    <Box sx={{ flex: 1 }}>
                        <Paper sx={{ background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)', color: 'white', height: '100%', borderRadius: 1.5 }}>
                            <Box sx={largePad}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="caption" sx={statLabelSx}>Total Applications</Typography>
                                        {bigStat(stats.total)}
                                    </Box>
                                    <Avatar sx={{ ...largeAvatarSx, flexShrink: 0 }}>
                                        <PeopleIcon sx={largeIconSx} />
                                    </Avatar>
                                </Stack>
                            </Box>
                        </Paper>
                    </Box>

                    <Box sx={{ flex: 1 }}>
                        <Paper sx={{ background: 'linear-gradient(135deg, #f57c00 0%, #ffb74d 100%)', color: 'white', height: '100%', borderRadius: 1.5 }}>
                            <Box sx={largePad}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="caption" sx={statLabelSx}>New</Typography>
                                        {bigStat(stats.new)}
                                    </Box>
                                    <Avatar sx={{ ...largeAvatarSx, flexShrink: 0 }}>
                                        <PersonAddIcon sx={largeIconSx} />
                                    </Avatar>
                                </Stack>
                            </Box>
                        </Paper>
                    </Box>

                    <Box sx={{ flex: 1 }}>
                        <Paper sx={{ background: 'linear-gradient(135deg, #2e7d32 0%, #66bb6a 100%)', color: 'white', height: '100%', borderRadius: 1.5 }}>
                            <Box sx={largePad}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="caption" sx={statLabelSx}>Approved for Hire</Typography>
                                        {bigStat(stats.approved)}
                                    </Box>
                                    <Avatar sx={{ ...largeAvatarSx, flexShrink: 0 }}>
                                        <CheckCircleIcon sx={largeIconSx} />
                                    </Avatar>
                                </Stack>
                            </Box>
                        </Paper>
                    </Box>

                    <Box sx={{ flex: 1 }}>
                        <Paper sx={{ background: 'linear-gradient(135deg, #6a1b9a 0%, #ba68c8 100%)', color: 'white', height: '100%', borderRadius: 1.5 }}>
                            <Box sx={largePad}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="caption" sx={statLabelSx}>Hired</Typography>
                                        {bigStat(stats.hired)}
                                    </Box>
                                    <Avatar sx={{ ...largeAvatarSx, flexShrink: 0 }}>
                                        <HowToRegIcon sx={largeIconSx} />
                                    </Avatar>
                                </Stack>
                            </Box>
                        </Paper>
                    </Box>

                    <Box sx={{ flex: 1 }}>
                        <Paper sx={{ background: 'linear-gradient(135deg, #7b1fa2 0%, #ba68c8 100%)', color: 'white', height: '100%', borderRadius: 1.5 }}>
                            <Box sx={largePad}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="caption" sx={statLabelSx}>In Review</Typography>
                                        {bigStat(stats.in_review)}
                                    </Box>
                                    <Avatar sx={{ ...largeAvatarSx, flexShrink: 0 }}>
                                        <AssignmentIndIcon sx={largeIconSx} />
                                    </Avatar>
                                </Stack>
                            </Box>
                        </Paper>
                    </Box>
                </Stack>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.25, rowGap: 1, columnGap: 1 }}>
                    <Box sx={{ flex: 1 }}>
                        <Paper sx={{ background: 'linear-gradient(135deg, #00695c 0%, #4db6ac 100%)', color: 'white', height: '100%', borderRadius: 1.5 }}>
                            <Box sx={smallCardSx}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="caption" sx={{ ...statLabelSx, fontSize: '0.6rem' }}>Avg. Experience</Typography>
                                        {smallStat(avgExperience !== '—' ? `${avgExperience} yr` : '—')}
                                    </Box>
                                    <Avatar sx={{ ...smallAvatarSx, flexShrink: 0 }}>
                                        <LocalShippingIcon sx={smallIconSx} />
                                    </Avatar>
                                </Stack>
                            </Box>
                        </Paper>
                    </Box>

                    <Box sx={{ flex: 1 }}>
                        <Tooltip title="View all applicants on a map" arrow>
                            <Paper
                                onClick={() => setMapOpen(true)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        setMapOpen(true);
                                    }
                                }}
                                sx={{
                                    background: 'linear-gradient(135deg, #37474f 0%, #78909c 100%)',
                                    color: 'white',
                                    height: '100%',
                                    cursor: 'pointer',
                                    position: 'relative',
                                    borderRadius: 1.5,
                                    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                                    '&:hover': {
                                        transform: 'translateY(-1px)',
                                        boxShadow: 4,
                                    },
                                    '&:focus-visible': {
                                        outline: '2px solid #fff',
                                        outlineOffset: 2,
                                    },
                                }}
                            >
                                <Box sx={{
                                    position: 'absolute',
                                    top: 4,
                                    right: 4,
                                    bgcolor: 'rgba(255,255,255,0.18)',
                                    borderRadius: '50%',
                                    width: 18,
                                    height: 18,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}>
                                    <MapIcon sx={{ fontSize: 12 }} />
                                </Box>
                                <Box sx={smallCardSx}>
                                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                                        <Box sx={{ minWidth: 0, pr: 2 }}>
                                            <Typography variant="caption" sx={{ ...statLabelSx, fontSize: '0.6rem' }}>Top State</Typography>
                                            {smallStat(topState ? topState[0] : '—')}
                                            {topState && (
                                                <Typography variant="caption" sx={{ opacity: 0.85, fontSize: '0.65rem', display: 'block', lineHeight: 1.2 }}>
                                                    {topState[1]} applicant{topState[1] !== 1 ? 's' : ''}
                                                </Typography>
                                            )}
                                            <Typography variant="caption" sx={{ display: 'block', opacity: 0.85, mt: 0.125, fontStyle: 'italic', fontSize: '0.6rem' }}>
                                                Click to view map
                                            </Typography>
                                        </Box>
                                        <Avatar sx={{ ...smallAvatarSx, flexShrink: 0 }}>
                                            <MapIcon sx={smallIconSx} />
                                        </Avatar>
                                    </Stack>
                                </Box>
                            </Paper>
                        </Tooltip>
                    </Box>

                    <Box sx={{ flex: 1 }}>
                        <Paper sx={{ background: 'linear-gradient(135deg, #4527a0 0%, #9575cd 100%)', color: 'white', height: '100%', borderRadius: 1.5 }}>
                            <Box sx={smallCardSx}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="caption" sx={{ ...statLabelSx, fontSize: '0.6rem' }}>Top CDL Class</Typography>
                                        {smallStat(topCdl ? `Class ${topCdl[0]}` : '—')}
                                        {topCdl && (
                                            <Typography variant="caption" sx={{ opacity: 0.85, fontSize: '0.65rem', display: 'block', lineHeight: 1.2 }}>
                                                {Math.round((topCdl[1] / cdlValues.length) * 100)}% of applicants
                                            </Typography>
                                        )}
                                    </Box>
                                    <Avatar sx={{ ...smallAvatarSx, flexShrink: 0 }}>
                                        <BadgeIcon sx={smallIconSx} />
                                    </Avatar>
                                </Stack>
                            </Box>
                        </Paper>
                    </Box>

                    <Box sx={{ flex: 1 }}>
                        <Paper sx={{ background: 'linear-gradient(135deg, #ad1457 0%, #f06292 100%)', color: 'white', height: '100%', borderRadius: 1.5 }}>
                            <Box sx={smallCardSx}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="caption" sx={{ ...statLabelSx, fontSize: '0.6rem' }}>Avg. Age</Typography>
                                        {smallStat(avgAge !== '—' ? `${avgAge} yr` : '—')}
                                        {ages.length > 0 && ages.length < resumes.length && (
                                            <Typography variant="caption" sx={{ opacity: 0.85, fontSize: '0.65rem', display: 'block', lineHeight: 1.2 }}>
                                                {ages.length} of {resumes.length} reported
                                            </Typography>
                                        )}
                                    </Box>
                                    <Avatar sx={{ ...smallAvatarSx, flexShrink: 0 }}>
                                        <CakeIcon sx={smallIconSx} />
                                    </Avatar>
                                </Stack>
                            </Box>
                        </Paper>
                    </Box>
                </Stack>
            </Box>
        );
    };

    const SortableApplicationCard = ({ item }) => {
        const {
            attributes,
            listeners,
            setNodeRef,
            transform,
            transition,
            isDragging,
        } = useSortable({ 
            id: item._id,
            data: {
                type: 'application',
                status: item.status,
                item: item
            }
        });

        const style = {
            transform: CSS.Transform.toString(transform),
            transition,
            opacity: isDragging ? 0 : 1, // Hide the original when dragging
            cursor: 'grab',
            marginBottom: 8,
            zIndex: isDragging ? 1000 : 'auto',
            position: 'relative'
        };

        return (
            <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
                {renderApplicationCard(item)}
            </div>
        );
    };

    const DroppableColumn = ({ column, items }) => {
        const { setNodeRef, isOver } = useDroppable({
            id: column.id,
            data: {
                type: 'column',
                status: column.id
            }
        });

        return (
            <Paper
                key={column.id}
                sx={{
                    minWidth: { xs: 220, sm: 240 },
                    maxWidth: { xs: 220, sm: 260 },
                    flex: '1 0 auto',
                    borderTop: '2px solid',
                    borderTopColor: column.color,
                    borderRadius: 1.5,
                    backgroundColor: isOver ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
                    border: isOver ? '2px dashed #1976d2' : '2px solid transparent'
                }}
            >
                <Box sx={{ px: 1.25, py: 1, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Stack direction="row" alignItems="center" spacing={0.75} mb={0.5}>
                        <Avatar sx={{ bgcolor: column.color, width: 26, height: 26, '& .MuiSvgIcon-root': { fontSize: 16 } }}>
                            {column.icon}
                        </Avatar>
                        <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                            {column.title}
                        </Typography>
                    </Stack>
                    {ready ? (
                        <Chip label={`${items.length}`} title={`${items.length} applicant${items.length !== 1 ? 's' : ''}`} size="small" sx={{ height: 22, '& .MuiChip-label': { px: 1, fontSize: '0.7rem' } }} />
                    ) : (
                        <Skeleton variant="rounded" width={28} height={22} />
                    )}
                </Box>

                <SortableContext 
                    items={items.map(item => item._id)} 
                    strategy={verticalListSortingStrategy} 
                    id={column.id}
                >
                    <Box 
                        ref={setNodeRef}
                        sx={{ 
                            p: 1, 
                            minHeight: 280, 
                            maxHeight: 'min(58vh, 520px)', 
                            overflowY: 'auto',
                            position: 'relative',
                            border: '2px solid transparent',
                            '&:hover': {
                                border: '2px dashed #1976d2',
                                backgroundColor: 'rgba(25, 118, 210, 0.04)'
                            }
                        }}
                        data-droppable-id={column.id}
                        data-column-id={column.id}
                        data-column-title={column.title}
                    >
                        {!ready ? (
                            <BoardCardSkeletons />
                        ) : items.length === 0 ? (
                            <Box
                                sx={{
                                    p: 2,
                                    borderRadius: 1,
                                    border: '2px dashed',
                                    borderColor: 'divider',
                                    textAlign: 'center',
                                    minHeight: 120,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <Typography variant="caption" color="text.secondary">
                                    Drop here
                                </Typography>
                            </Box>
                        ) : (
                            items.map(item => <SortableApplicationCard key={item._id} item={item} />)
                        )}
                    </Box>
                </SortableContext>
            </Paper>
        );
    };

    const renderApplicationCard = (item) => (
        <Card
            key={item._id}
            sx={{
                mb: 1,
                cursor: 'pointer',
                transition: 'all 0.2s',
                border: !item.viewedAt ? '2px solid #1976d2' : '1px solid #e0e0e0',
                '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: 3
                }
            }}
            onClick={() => handleCardClick(item)}
        >
            <CardContent sx={{ py: 1, px: 1.25, '&:last-child': { pb: 1 } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                        {!item.viewedAt && (
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'info.main', flexShrink: 0 }} />
                        )}
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.3 }} noWrap title={`${item.firstName} ${item.lastName}`}>
                            {item.firstName} {item.lastName}
                        </Typography>
                    </Box>
                    <IconButton size="small" onClick={(e) => handleDeleteRequest(item, e)} sx={{ color: 'error.main', p: 0.25 }} aria-label="Delete">
                        <DeleteIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Stack>

                <Stack spacing={0.35}>
                    {item.email && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                            <EmailIcon sx={{ fontSize: 14 }} color="action" />
                            <Typography variant="caption" color="text.secondary" noWrap title={item.email}>{item.email}</Typography>
                        </Box>
                    )}
                    {item.phone && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PhoneIcon sx={{ fontSize: 14 }} color="action" />
                            <Typography variant="caption" color="text.secondary">{item.phone}</Typography>
                        </Box>
                    )}
                    {item.sourcePayload?.location?.city && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                            <LocationOnIcon sx={{ fontSize: 14 }} color="action" />
                            <Typography variant="caption" color="text.secondary" noWrap>
                                {item.sourcePayload.location.city}
                                {item.sourcePayload.location.state ? `, ${item.sourcePayload.location.state}` : ''}
                            </Typography>
                        </Box>
                    )}
                    {item.sourcePayload?.cdl_class && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <WorkIcon sx={{ fontSize: 14 }} color="action" />
                            <Typography variant="caption" color="text.secondary">
                                CDL {item.sourcePayload.cdl_class}
                            </Typography>
                        </Box>
                    )}
                    {flaggedResumeIds.has(String(item._id)) && (
                        <Chip label="Needs a look" size="small" color="warning" sx={{ alignSelf: 'flex-start' }} />
                    )}
                    {item.referralKind === 'exact' && (
                        <Chip label="Referral" size="small" sx={{ alignSelf: 'flex-start', bgcolor: '#2e7d32', color: '#fff', fontWeight: 700 }} />
                    )}
                    {item.referralKind === 'possible' && (
                        <Chip label="Possible referral" size="small" color="warning" sx={{ alignSelf: 'flex-start', fontWeight: 700 }} />
                    )}
                </Stack>

                {item.notes && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, fontStyle: 'italic', lineClamp: 2, overflow: 'hidden' }}>
                        Note: {item.notes}
                    </Typography>
                )}
            </CardContent>
            <CardActions sx={{ pt: 0, px: 1.25, pb: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                    {new Date(item.createdAt).toLocaleDateString()}
                </Typography>
            </CardActions>
        </Card>
    );

    return (
        <Container maxWidth="xl" disableGutters sx={{ pt: 0, pb: { xs: 0.5, md: 1 } }}>
            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {renderStatsCards()}

            {/* Controls */}
            <Paper sx={{ p: 1, mb: 1.5, borderRadius: 2, borderTop: '3px solid', borderTopColor: 'primary.main' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Recruitment Pipeline
                    </Typography>
                    <Stack direction="row" spacing={1}>
                        <Button
                            size="small"
                            variant="contained"
                            startIcon={<PersonAddIcon />}
                            onClick={() => setManualOpen(true)}
                        >
                            Add applicant
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<MapIcon />}
                            onClick={() => setMapOpen(true)}
                        >
                            View on Map
                        </Button>
                        <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={fetchData}>
                            Refresh
                        </Button>
                        <Button
                            size="small"
                            startIcon={<FilterListIcon />}
                            onClick={() => setShowFilters(!showFilters)}
                            variant={showFilters ? "contained" : "outlined"}
                        >
                            Filters
                        </Button>
                    </Stack>
                </Stack>

                <Collapse in={showFilters}>
                    <Stack spacing={2} sx={{ mb: 2 }}>
                        <TextField
                            size="small"
                            placeholder="Search by name, email, phone..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            InputProps={{
                                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                            }}
                        />
                    </Stack>
                </Collapse>
            </Paper>

            {/* Kanban Board */}
            <DndContext 
                sensors={sensors} 
                collisionDetection={closestCenter} 
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
            >
                <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1.5, mx: -0.5, px: 0.5 }}>
                    {columns.map((column) => {
                        const items = getItemsByStatus(column.id);
                        return (
                            <DroppableColumn 
                                key={column.id}
                                column={column} 
                                items={items} 
                            />
                        );
                    })}
                </Box>
                
                <DragOverlay>
                    {activeId ? (
                        <Box sx={{ 
                            transform: 'rotate(5deg)',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                            borderRadius: 2,
                            overflow: 'hidden'
                        }}>
                            {renderApplicationCard(resumes.find(r => r._id === activeId))}
                        </Box>
                    ) : null}
                </DragOverlay>
            </DndContext>

            {/* Applicant Map Dialog */}
            <ApplicantMapDialog
                open={mapOpen}
                onClose={() => setMapOpen(false)}
                onApplicantClick={(id) => {
                    const item = resumes.find((r) => r._id === id);
                    if (item) {
                        setMapOpen(false);
                        handleCardClick(item);
                    }
                }}
            />

            {/* Detail Dialog */}
            <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">Application Details</Typography>
                    </Stack>
                </DialogTitle>
                <DialogContent dividers data-usage-screen="recruitment.application">
                    {detailItem && (
                        <ApplicationDetailContent
                            detailItem={detailItem}
                            setDetailItem={setDetailItem}
                            handleUpdate={handleUpdate}
                            flags={openFlags.filter((flag) => String(flag.sourceId) === String(detailItem._id))}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation */}
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningIcon color="error" />
                    Delete Application?
                </DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to delete this application? This action cannot be undone.</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDeleteConfirm}>Delete</Button>
                </DialogActions>
            </Dialog>

            {/* Manual Add Applicant */}
            <Dialog
                open={manualOpen}
                onClose={() => { if (!manualSaving) { setManualOpen(false); resetManualForm(); } }}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Add applicant</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2}>
                        {manualError && (
                            <Alert severity="error" onClose={() => setManualError(null)}>
                                {manualError}
                            </Alert>
                        )}
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField
                                label="First name"
                                required
                                fullWidth
                                size="small"
                                value={manualForm.firstName}
                                onChange={(e) => setManualForm({ ...manualForm, firstName: e.target.value })}
                                error={!manualForm.firstName.trim() && !!manualError}
                            />
                            <TextField
                                label="Last name"
                                required
                                fullWidth
                                size="small"
                                value={manualForm.lastName}
                                onChange={(e) => setManualForm({ ...manualForm, lastName: e.target.value })}
                                error={!manualForm.lastName.trim() && !!manualError}
                            />
                        </Stack>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField
                                label="Email"
                                fullWidth
                                size="small"
                                type="email"
                                autoComplete="email"
                                value={manualForm.email}
                                onChange={(e) => setManualForm({ ...manualForm, email: e.target.value })}
                                error={!!emailError}
                                helperText={emailError || ' '}
                            />
                            <TextField
                                label="Phone"
                                fullWidth
                                size="small"
                                type="tel"
                                autoComplete="tel"
                                value={manualForm.phone}
                                onChange={(e) => setManualForm({ ...manualForm, phone: e.target.value })}
                                error={!!phoneError}
                                helperText={phoneError || (phonePreview ? `Will be saved as ${phonePreview}` : ' ')}
                            />
                        </Stack>
                        <FormControl fullWidth size="small">
                            <InputLabel>Status</InputLabel>
                            <Select
                                label="Status"
                                value={manualForm.status}
                                onChange={(e) => setManualForm({ ...manualForm, status: e.target.value })}
                            >
                                <MenuItem value="new">New</MenuItem>
                                <MenuItem value="in_review">In Review</MenuItem>
                                <MenuItem value="approved">Approved for Hire</MenuItem>
                                <MenuItem value="hired">Hired</MenuItem>
                                <MenuItem value="rejected">Rejected</MenuItem>
                            </Select>
                        </FormControl>

                        <Divider />
                        <Box>
                            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                                Demographics (optional)
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                Used for pipeline stats: Avg. experience, top state, top CDL class, and avg. age.
                            </Typography>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <TextField
                                    label="Years of experience"
                                    fullWidth
                                    size="small"
                                    type="number"
                                    inputProps={{ min: 0, max: 80, step: 0.1 }}
                                    value={manualForm.experience_years}
                                    onChange={(e) => setManualForm({ ...manualForm, experience_years: e.target.value })}
                                    error={!!expError}
                                    helperText={expError || 'e.g. 14.6'}
                                />
                                <FormControl fullWidth size="small">
                                    <InputLabel>State (residence)</InputLabel>
                                    <Select
                                        label="State (residence)"
                                        value={manualForm.locationState || ''}
                                        onChange={(e) => setManualForm({ ...manualForm, locationState: e.target.value })}
                                    >
                                        <MenuItem value="">
                                            <em>Not specified</em>
                                        </MenuItem>
                                        {US_STATE_CODES.map((code) => (
                                            <MenuItem key={code} value={code}>{code}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Stack>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>CDL class</InputLabel>
                                    <Select
                                        label="CDL class"
                                        value={manualForm.cdl_class || ''}
                                        onChange={(e) => setManualForm({ ...manualForm, cdl_class: e.target.value })}
                                    >
                                        <MenuItem value="">
                                            <em>Not specified</em>
                                        </MenuItem>
                                        <MenuItem value="A">A</MenuItem>
                                        <MenuItem value="B">B</MenuItem>
                                        <MenuItem value="C">C</MenuItem>
                                        <MenuItem value="A/B">A/B</MenuItem>
                                        <MenuItem value="Other">Other</MenuItem>
                                    </Select>
                                </FormControl>
                                <TextField
                                    label="Date of birth"
                                    fullWidth
                                    size="small"
                                    type="date"
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ max: new Date().toISOString().slice(0, 10) }}
                                    value={manualForm.date_of_birth}
                                    onChange={(e) => setManualForm({ ...manualForm, date_of_birth: e.target.value })}
                                    helperText="For average age on the dashboard"
                                />
                            </Stack>
                        </Box>

                        <TextField
                            label="Notes"
                            fullWidth
                            size="small"
                            multiline
                            rows={3}
                            value={manualForm.notes}
                            onChange={(e) => setManualForm({ ...manualForm, notes: e.target.value })}
                            placeholder="Internal notes (optional)"
                        />

                        <Box>
                            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                                Resume file (optional)
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                Drag &amp; drop or click to browse. PDF, DOC, or DOCX up to {formatBytes(RESUME_MAX_BYTES)}.
                            </Typography>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                style={{ display: 'none' }}
                                onChange={(e) => {
                                    handleManualFileSelected(e.target.files?.[0] || null);
                                    e.target.value = '';
                                }}
                            />
                            {manualFile ? (
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 1.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        borderColor: 'success.light',
                                        bgcolor: 'success.light',
                                        backgroundImage: 'linear-gradient(rgba(255,255,255,0.85), rgba(255,255,255,0.85))',
                                    }}
                                >
                                    <InsertDriveFileIcon color="success" />
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography
                                            variant="body2"
                                            sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                            title={manualFile.name}
                                        >
                                            {manualFile.name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {formatBytes(manualFile.size)}
                                            {manualFile.type ? ` • ${manualFile.type}` : ''}
                                        </Typography>
                                    </Box>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleManualFileSelected(null)}
                                        aria-label="Remove file"
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </Paper>
                            ) : (
                                <Box
                                    onClick={() => fileInputRef.current?.click()}
                                    onDragOver={handleDragOver}
                                    onDragEnter={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={handleDrop}
                                    sx={{
                                        cursor: 'pointer',
                                        border: '2px dashed',
                                        borderColor: dragActive ? 'primary.main' : (manualFileError ? 'error.main' : 'divider'),
                                        bgcolor: dragActive ? 'rgba(25, 118, 210, 0.06)' : 'background.default',
                                        borderRadius: 2,
                                        p: 3,
                                        textAlign: 'center',
                                        transition: 'all 0.15s ease-in-out',
                                        '&:hover': {
                                            borderColor: 'primary.main',
                                            bgcolor: 'rgba(25, 118, 210, 0.04)',
                                        },
                                    }}
                                >
                                    <CloudUploadIcon sx={{ fontSize: 40, color: dragActive ? 'primary.main' : 'text.secondary', mb: 1 }} />
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {dragActive ? 'Drop the file here' : 'Drag & drop a resume here, or click to browse'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        PDF, DOC, or DOCX • up to {formatBytes(RESUME_MAX_BYTES)}
                                    </Typography>
                                </Box>
                            )}
                            {manualFileError && (
                                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                                    {manualFileError}
                                </Typography>
                            )}
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => { setManualOpen(false); resetManualForm(); }}
                        disabled={manualSaving}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleManualSubmit}
                        disabled={!canSubmitManual}
                    >
                        {manualSaving ? 'Saving…' : 'Add'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}

export default RecruitmentPage;
